import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { ApiCallStatus } from "../../utils/constants";
import TradeShow, { DEFAULT_YEAR } from "../../components/TradeShow";
import { fetchEventDataByName } from "../PageFunctions/eventFunctions";
import { createEventResponse } from "../PageFunctions/formFunctions";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import { omitBy, isNull } from "lodash"; // Import Lodash omitBy and isNull
import { toast } from "react-toastify";
// import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";

const TradeShowPage = ({ genericDataLoaded, genericPageContent }) => {
  const { isAuthenticated, isLoading } = useArgusAuth();
  const { year } = useParams();
  const selectedYear = year || DEFAULT_YEAR;

  const [eventData, setEventData] = useState(null);
  const [fetchEventDataByNameCallStatus, setFetchEventDataByNameCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [createEventResponseCallStatus, setCreateEventResponseCallStatus] =
    useState(ApiCallStatus.NotStarted);
  const [isRsvpModalOpen, setIsRsvpModalOpen] = useState(false);

  const schema = yup
    .object({
      attending: yup
        .string()
        .required("Please indicate whether you are attending"),
      companyName: yup.string().required("Company name is required"),
      email: yup
        .string()
        .email("Email must be a valid email address")
        .required("Email is required"),
      accountNumber: yup.string(),
      guests: yup
        .number()
        .required("Please indicate how many guests you will be attending with")
        .min(1)
        .max(50),
    })
    .required();
  const {
    control,
    trigger,
    getValues,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      attending: "Yes",
      companyName: "",
      email: "",
      accountNumber: "",
      guests: 1,
    },
  });

  const fetchEventDataByNameAsync = async () => {
    // eventName is a substring match (see sp_Event_Find), so "Trade Show <selectedYear>" resolves
    // directly to that year's event — the year comes from the page's own URL segment, not a literal
    // that needs bumping in code each year.
    await fetchEventDataByName({
      eventName: `Trade Show ${selectedYear}`,
      setEventData,
      setStatusInProgress: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setFetchEventDataByNameCallStatus(ApiCallStatus.Error),
    });
  };

  const createEventResponseAsync = async ({
    attending,
    companyName,
    email,
    accountNumber,
    guests,
  }) => {
    if (!eventData?.eventId) {
      toast("This trade show isn't open for RSVPs yet.", {
        type: "error",
        autoClose: 5000,
      });
      return;
    }

    const eventResponse = {
      attending,
      companyName,
      email,
      accountNumber,
      guests,
    };

    // Removing null or undefined values from eventResponse
    const cleanResponse = omitBy(eventResponse, isNull);

    await createEventResponse({
      eventId: eventData.eventId,
      eventResponse: cleanResponse,
      setStatusInProgress: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Failed),
      setStatusError: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Error),
      setStatusDuplicateError: () =>
        setCreateEventResponseCallStatus(ApiCallStatus.Conflict),
    });
  };

  const unAuthorizedPageLoad = async () => {
    // Only the live campaign year (DEFAULT_YEAR) has an RSVP form — past years are static photo
    // galleries with no backing Event row at all, so searching for them would only ever fail.
    if (selectedYear === DEFAULT_YEAR) {
      await fetchEventDataByNameAsync();
    }
  };

  const onSubmitClick = async () => {
    const result = await trigger([
      "attending",
      "companyName",
      "email",
      "guests",
      "accountNumber",
    ]);
    if (!!result) {
      await createEventResponseAsync({
        attending: getValues("attending"),
        companyName: getValues("companyName"),
        email: getValues("email"),
        accountNumber: getValues("accountNumber")
          ? getValues("accountNumber")
          : null,
        // The Guests field is a native number input, so getValues() returns a string ("5") — cast it
        // back to a real number. The backend's duplicate check compares the whole submitted JSON
        // document and treats 5 and "5" as different values, so leaving this as a string silently
        // breaks duplicate-email detection (a number-typed submission never matches a string one).
        guests: Number(getValues("guests")),
      });
    }
  };

  useEffect(() => {
    if (genericDataLoaded === true && isLoading === false) {
      unAuthorizedPageLoad();
    }
  }, [genericDataLoaded, isLoading, selectedYear]);

  const allLoadingStates = [
    fetchEventDataByNameCallStatus,
    createEventResponseCallStatus,
  ];

  const pageIsLoading =
    allLoadingStates.includes(ApiCallStatus.InProgress) ||
    genericDataLoaded === false ||
    isLoading === true;

  return (
    <Container maxWidth="lg">
      {/* <Breadcrumbs /> */}

      {genericDataLoaded === true && isLoading === false && (
        <TradeShow
          onSubmitClick={onSubmitClick}
          control={control}
          errors={errors}
          genericDataLoaded={genericDataLoaded}
          createEventResponseCallStatus={createEventResponseCallStatus}
          setCreateEventResponseCallStatus={setCreateEventResponseCallStatus}
          setIsRsvpModalOpen={setIsRsvpModalOpen}
          reset={reset}
        />
      )}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={pageIsLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};
export default TradeShowPage;
